import type { KeyboardEvent } from "react";
import { useUi, useUiDispatch } from "@/app/AppContext";
import {
  getDomainStatus,
  normalizeSystemStatus,
  type StatusCounts,
  type SystemDomain,
  type SystemStatus,
} from "@/domain/systemAnalysis";
import { useI18n } from "@/i18n/useI18n";
import { pick, statusLabelKey } from "./useSystemAnalysis";

const COLLAPSED_ITEM_COUNT = 6;
const LONG_DESCRIPTION = 120;
const BUBBLE_ORDER: Exclude<SystemStatus, "unknown">[] = [
  "zero",
  "fulfilled",
  "almost_fulfilled",
  "partially_fulfilled",
  "only_limited",
  "not_fulfilled",
  "insufficient_information",
];

interface SystemGridProps {
  domains: SystemDomain[];
  /** Per-criterion location counts; shown as bubbles for "all locations". */
  locationCounts: Map<string, StatusCounts> | null;
}

export function SystemGrid({ domains, locationCounts }: SystemGridProps) {
  const { t, language } = useI18n();
  const { system } = useUi();
  const dispatch = useUiDispatch();

  return (
    <div id="system-grid" className="system-grid">
      {domains.map((domain) => {
        const domainStatus = getDomainStatus(domain);
        const expanded = system.expandedDomains.has(domain.id);
        const items = expanded ? domain.items : domain.items.slice(0, COLLAPSED_ITEM_COUNT);
        const description = pick(language, domain.descriptionNo, domain.descriptionEn);
        const descriptionExpanded = system.expandedDescriptions.has(domain.id);
        const descriptionToggles = description.length > LONG_DESCRIPTION;
        const toggleDescription = () =>
          dispatch({ type: "toggleSystemDescription", domainId: domain.id });
        const onDescriptionKey = (event: KeyboardEvent) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleDescription();
          }
        };

        return (
          <section key={domain.id} className="system-domain-card">
            <div className={`system-domain-header system-domain-header-${domainStatus}`}>
              <span
                className={`system-domain-badge system-status-${domainStatus}`}
                title={t(statusLabelKey(domainStatus))}
              >
                {domain.id}
              </span>
              <div>
                <h4 className="system-domain-title">
                  {pick(language, domain.titleNo, domain.titleEn)}
                </h4>
                {description &&
                  (descriptionToggles ? (
                    <div
                      className={`system-domain-description system-domain-description-clickable${descriptionExpanded ? "" : " system-domain-description-collapsed"}`}
                      role="button"
                      tabIndex={0}
                      aria-expanded={descriptionExpanded}
                      onClick={toggleDescription}
                      onKeyDown={onDescriptionKey}
                    >
                      {description}
                    </div>
                  ) : (
                    <div className="system-domain-description system-domain-description-collapsed">
                      {description}
                    </div>
                  ))}
              </div>
            </div>
            <div className="system-domain-items">
              {!domain.items.length && (
                <div className="system-empty-domain">{t("systemNoCriteria")}</div>
              )}
              {items.map((item) => {
                const status = normalizeSystemStatus(item.status);
                const counts = locationCounts?.get(item.id);
                const bubbles = counts ? BUBBLE_ORDER.filter((key) => counts[key] > 0) : [];
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`system-row${system.criterionId === item.id ? " selected" : ""}`}
                    aria-label={`${item.id} ${t(statusLabelKey(status))}`}
                    onClick={() =>
                      dispatch({
                        type: "selectSystemCriterion",
                        domainId: domain.id,
                        criterionId: item.id,
                      })
                    }
                  >
                    <span className="system-row-id">{item.id}</span>
                    <span className="system-row-title">
                      {pick(language, item.titleNo, item.titleEn)}
                    </span>
                    {bubbles.length ? (
                      <span className="system-row-score-bubbles">
                        {bubbles.map((key) => (
                          <span
                            key={key}
                            className={`system-row-score-bubble system-status-${key}`}
                            title={`${t(statusLabelKey(key))}: ${counts![key]}`}
                          >
                            {counts![key]}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span
                        className={`system-status-dot system-row-status system-status-${status}`}
                        title={t(statusLabelKey(status))}
                      />
                    )}
                    <span className="system-row-chevron" aria-hidden="true">
                      ›
                    </span>
                  </button>
                );
              })}
            </div>
            {domain.items.length > COLLAPSED_ITEM_COUNT && (
              <div className="system-domain-footer">
                <button
                  type="button"
                  className="system-show-all-btn"
                  onClick={() => dispatch({ type: "toggleSystemDomain", domainId: domain.id })}
                >
                  {`${expanded ? t("systemHideAll") : t("systemShowAll")} (${domain.items.length})`}
                </button>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
